import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ProjectDTO } from '../DTO/ProjectDTO';
import { assetUrl } from '../config/config';
import { formatPeriod } from '../lib/format';
import { PageHeader } from './PageHeader';
import { TrailEnd, TrailStart, Waypoint } from './Section';
import { StatusLabel } from './StatusLabel';
import { ProjectListSkeleton } from './Skeleton';
import { OptionalImage } from './OptionalImage';

const byNewest = (a: ProjectDTO, b: ProjectDTO) =>
    (b.startDate ?? '').localeCompare(a.startDate ?? '');

// ── ProjectEntry: one project as a waypoint on the trail ──────────────────────
function ProjectEntry({ project }: { project: ProjectDTO }) {
    const href = `/projects/${project.slug}`;
    const year = project.startDate ? new Date(project.startDate).getUTCFullYear() : null;

    return (
        <Waypoint
            as="article"
            marker={
                <>
                    {year && <span className="font-display text-2xl md:text-[1.75rem] font-semibold leading-tight text-ink">{year}</span>}
                    <span className="label">{formatPeriod(project.startDate, project.endDate)}</span>
                    {project.status && <StatusLabel status={project.status} />}
                </>
            }
        >
            {/* Lead image: the screenshot first, then the text (DESIGN.md → Projects list) */}
            {project.thumbnailUrl && (
                <OptionalImage
                    src={assetUrl(project.thumbnailUrl)}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                    frame={img => (
                        <Link
                            to={href}
                            tabIndex={-1}
                            aria-hidden
                            className="block max-w-[44rem] mb-5 overflow-hidden rounded border border-rule bg-surface aspect-[2/1] hover:border-muted transition-colors"
                        >
                            {img}
                        </Link>
                    )}
                />
            )}

            <div className="flex flex-col gap-3">
                <h2 className="text-2xl md:text-[1.75rem] m-0">
                    <Link to={href} className="hover:text-loden">{project.name}</Link>
                </h2>

                <p className="text-muted m-0 max-w-[40rem]">{project.description}</p>

                {project.technologies?.length > 0 && (
                    <ul className="flex flex-wrap gap-2 list-none p-0 m-0" aria-label="Technologies">
                        {project.technologies.map(t => (
                            <li key={t.id} className="tag">{t.name}</li>
                        ))}
                    </ul>
                )}

                <Link to={href} className="link font-label self-start mt-1">
                    View project<span className="sr-only">: {project.name}</span>
                </Link>
            </div>
        </Waypoint>
    );
}

// ── ProjectsList ──────────────────────────────────────────────────────────────
export function ProjectsList() {
    const [projects, setProjects] = useState<ProjectDTO[]>([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(false);

    useEffect(() => {
        axios.get<ProjectDTO[]>('/api/projects')
            .then(res => setProjects([...res.data].sort(byNewest)))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    const header = (
        <PageHeader
            title="Projects"
            lead="Things I've built, shipped, and learned from."
        />
    );

    if (loading) return <ProjectListSkeleton />;

    if (error || projects.length === 0) {
        return (
            <>
                {header}
                <p className="page-container py-16 text-muted">
                    {error
                        ? 'The projects could not be loaded. Reload the page to try again.'
                        : 'No projects published yet.'}
                </p>
            </>
        );
    }

    return (
        <>
            {header}
            <TrailStart label={`${projects.length} ${projects.length === 1 ? 'project' : 'projects'}, newest first`} />
            {projects.map(p => <ProjectEntry key={p.id} project={p} />)}
            <TrailEnd />
            <div className="pb-12" />
        </>
    );
}
